import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./EditingGallery.css";
import { FaEye, FaPaperPlane, FaUser } from "react-icons/fa";
import Loader2 from "../../../../Template/Loader/Loader2";
import { S3UploadService } from "../../UploadGallery/GalleryUploadCard/S3UploadServices";
import { getBookingByIdAPI } from "../../../../utils/APIs/bookingsApis";

const EditingGallery = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState("standard");
  const [activeSubTab, setActiveSubTab] = useState("receiver");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const stored = sessionStorage.getItem("isSidebarOpen");
    return stored !== null ? JSON.parse(stored) : true;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const stored = sessionStorage.getItem("isSidebarOpen");
      const parsed = stored !== null ? JSON.parse(stored) : true;

      if (parsed !== isSidebarOpen) {
        setIsSidebarOpen(parsed);
      }
    }, 10);

    return () => clearInterval(interval);
  }, [isSidebarOpen]);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const res = await getBookingByIdAPI(bookingId);
        if (res?.data?.success) {
          setBookingDetails(res.data.data);
        }
      } catch (error) {
        console.error("Fetch Booking Error:", error);
      }
    };
    if (bookingId) fetchBooking();
  }, [bookingId]);

  const handleBack = () => navigate(-1);

  const handleViewGallery = () => {
    navigate(`/bookings/editing-view-gallery/${bookingId}`);
  };

  const handleFileSelect = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmitUpload = async () => {
    if (files.length === 0 || !bookingDetails) return;

    setUploading(true);
    setUploadProgress(0);

    const uploadService = new S3UploadService({
      onProgress: ({ percent }) => {
        setUploadProgress(percent);
      },
      onError: (err) => {
        console.error("Upload error:", err);
        alert(`Upload failed for a file: ${err.message}`);
      }
    });

    try {
      for (let i = 0; i < files.length; i++) {
        setCurrentFileIndex(i + 1);
        const file = files[i];
        
        await uploadService.upload(file, bookingDetails.veroaBookingId || bookingId, {
          bookingid: bookingDetails._id || bookingId,
          clientId: bookingDetails.clientId?._id || bookingDetails.clientId,
          photographerId: activeSubTab === "receiver" ? null : (bookingDetails.photographerId?._id || bookingDetails.photographerId),
          category: activeMainTab
        });
      }

      alert("All files uploaded successfully!");
      setFiles([]);
    } catch (error) {
      console.error("Overall Upload Error:", error);
    } finally {
      setUploading(false);
      setCurrentFileIndex(0);
      setUploadProgress(0);
    }
  };

  if (uploading) return (
    <Loader2 
      percentage={uploadProgress} 
      statusText={`Uploading file ${currentFileIndex} of ${files.length}...`} 
    />
  );

  return (
    <div
      className={`content-container ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
      style={{ marginTop: "100px" }}
    >
      <div className="page-inner-wrapper">
        <h2 className="mb-4">Gallery</h2>
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="gallery-container">
              {/* HEADER */}
              <div className="gallery-header">
                <button onClick={handleBack} className="admin-back-btn">
                  ← Back
                </button>
                <button onClick={handleViewGallery} className="admin-back-btn">
                  <FaEye style={{ marginRight: "6px" }} />
                  View Gallery
                </button>
              </div>

              {/* BOOKING INFO */}
              <h2>{bookingDetails?.service_id?.serviceName || "Editing Service"}</h2>
              <p className="client-name">{bookingDetails?.client_id?.username || "Client"}</p>
              <p>Event Location - {bookingDetails?.address || bookingDetails?.city || "N/A"}</p>
              <p>Event Date - {bookingDetails?.bookingDate ? new Date(bookingDetails.bookingDate).toLocaleDateString() : "N/A"}</p>
              <p>Photographer - {bookingDetails?.photographer_id?.basicInfo?.fullName || "N/A"}</p>
              <p>Booking ID - {bookingDetails?.veroaBookingId || bookingId}</p>

              {/* TABS */}
              {/* MAIN TABS */}
              <div className="custom-tabs mb-2 mt-4">
                <div
                  className={`tab-item ${activeMainTab === "standard" ? "active" : ""}`}
                  onClick={() => setActiveMainTab("standard")}
                >
                  Standard
                </div>
                <div
                  className={`tab-item ${activeMainTab === "premium" ? "active" : ""}`}
                  onClick={() => setActiveMainTab("premium")}
                >
                  Premium
                </div>
              </div>

              {/* SUB TABS */}
              <div className="custom-tabs sub-tabs mb-4">
                <div
                  className={`tab-item ${activeSubTab === "receiver" ? "active" : ""}`}
                  onClick={() => setActiveSubTab("receiver")}
                >
                  <FaUser className="me-2" /> User Videos
                </div>
                <div
                  className={`tab-item ${activeSubTab === "sender" ? "active" : ""}`}
                  onClick={() => setActiveSubTab("sender")}
                >
                  <FaPaperPlane className="me-2" /> Editor Videos
                </div>
              </div>

              {/* UPLOAD BOX */}
              <div 
                className="upload-box"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <p>Drag and Drop photos/videos here for <strong>{activeSubTab === "receiver" ? "User" : "Editor"} - {activeMainTab}</strong></p>
                <span>OR</span>
                <div className="d-flex justify-content-center gap-2">
                  <label className="upload-btn">
                    Select Files
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*,video/*"
                      onChange={handleFileSelect}
                    />
                  </label>
                  <label className="upload-btn">
                    Select Folder
                    <input
                      type="file"
                      hidden
                      multiple
                      webkitdirectory="true"
                      onChange={handleFileSelect}
                    />
                  </label>
                </div>

                {files.length > 0 && (
                  <button 
                    className="upload-btn mt-4" 
                    style={{ background: "var(--primary-color)", color: "#fff" }}
                    onClick={handleSubmitUpload}
                  >
                    Upload Files ({files.length})
                  </button>
                )}
              </div>

              {activeSubTab === "receiver" && (
                <div className="text-center mt-3">
                  <button 
                    className="admin-back-btn mx-auto" 
                    onClick={handleViewGallery}
                  >
                    <FaEye className="me-2" /> View Received Files
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditingGallery;
